import React, { useState, useEffect } from 'react';

import LoadingSvg from '@/components/common/LoadingSvg';
import { DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progressbar';

import { useMonthlyBreakdown } from '../hooks/useEasyPaySchedule';

type EasyPayMonthlyBreakdownDialogProps = {
    yearMonth: string | null;
    displayMonth: string | null;
};

function parseNoOfEasyPay(noOfEasyPay: string): [number, number] {
    const parts = noOfEasyPay.trim().split(' of ');
    if (parts.length !== 2) return [0, 0];

    const current = Number(parts[0]);
    const total = Number(parts[1]);

    if (isNaN(current) || isNaN(total)) return [0, 0];

    return [current, total];
}

export const EasyPayMonthlyBreakdownDialog: React.FC<EasyPayMonthlyBreakdownDialogProps> = ({
    yearMonth,
    displayMonth,
}) => {
    const { data: breakdownData, isLoading, error } = useMonthlyBreakdown(yearMonth || undefined);
    const [isAnimating, setIsAnimating] = useState(true);

    // Trigger animation when data changes
    useEffect(() => {
        if (breakdownData) {
            setIsAnimating(true);
            // Small delay to allow DOM to render with 0, then animate
            const timer = setTimeout(() => {
                setIsAnimating(false);
            }, 50);
            return () => clearTimeout(timer);
        }
    }, [breakdownData]);

    // Early return if no month selected
    if (!yearMonth || !displayMonth) {
        return (
            <div className="flex min-h-96 items-center justify-center">
                <div className="text-center text-gray-500">No month selected</div>
            </div>
        );
    }

    // Loading state
    if (isLoading) {
        return (
            <div className="" id="dialog-monthly-breakdown">
                <DialogHeader>
                    <DialogTitle className="mt-1.5 text-center font-bold">Monthly Breakdown</DialogTitle>
                    <DialogDescription className="text-center font-light">{displayMonth}</DialogDescription>
                </DialogHeader>
                <div className="flex min-h-96 items-center justify-center bg-white">
                    <div className="flex flex-col items-center">
                        <LoadingSvg />
                        Loading Monthly Breakdown
                    </div>
                </div>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className="" id="dialog-monthly-breakdown">
                <DialogHeader>
                    <DialogTitle className="mt-1.5 text-center font-bold">Monthly Breakdown</DialogTitle>
                    <DialogDescription className="text-center font-light">{displayMonth}</DialogDescription>
                </DialogHeader>
                <div className="flex min-h-96 items-center justify-center">
                    <div className="text-center text-red-600">Error loading monthly breakdown</div>
                </div>
            </div>
        );
    }

    // No data or empty array
    if (!breakdownData || breakdownData.length === 0) {
        return (
            <div className="" id="dialog-monthly-breakdown">
                <DialogHeader>
                    <DialogTitle className="mt-1.5 text-center font-bold">Monthly Breakdown</DialogTitle>
                    <DialogDescription className="text-center font-light">{displayMonth}</DialogDescription>
                </DialogHeader>
                <div className="flex min-h-96 items-center justify-center">
                    <div className="text-center text-gray-500">No payments scheduled for this month</div>
                </div>
            </div>
        );
    }

    // Calculate total amount due
    const total = breakdownData.reduce((sum, item) => sum + item.AmountDue, 0);
    const formattedTotal = `$${total.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

    return (
        <div className="" id="dialog-monthly-breakdown">
            <DialogHeader>
                <DialogTitle className="mt-1.5 text-center font-bold">{displayMonth}</DialogTitle>
                <DialogDescription className="text-center font-light">Amount Remaining</DialogDescription>
            </DialogHeader>
            <div className="mt-4 w-full">
                <div className="mx-auto grid max-w-[460px] grid-cols-[1fr_1fr_.5fr] text-xs font-light leading-6 md:text-sm md:leading-6 md2:grid-cols-[1fr_1fr_1fr_.5fr] lg:grid-cols-[1r_1fr_1fr_.5fr]">
                    {/* Mobile/Tablet Headers */}
                    <div
                        style={{ borderBottom: '1px solid #000' }}
                        className="py-2 pr-1 text-xxs font-bold uppercase leading-5 text-black xsm:text-xs xsm:leading-5 md2:hidden"
                    >
                        Bill Date <br /> Order Number
                    </div>
                    <div
                        style={{ borderBottom: '1px solid #000' }}
                        className="py-2 pr-1 text-xxs font-bold uppercase leading-5 text-black xsm:text-xs xsm:leading-5 md2:hidden"
                    >
                        Payment Progress
                    </div>
                    <div
                        style={{ borderBottom: '1px solid #000' }}
                        className="py-2 text-right text-xxs font-bold uppercase leading-5 text-black xsm:text-xs xsm:leading-5 md2:hidden"
                    >
                        Amount
                    </div>

                    {/* Desktop Headers */}
                    <div
                        className="hidden py-2 font-bold uppercase text-black md:text-xs md2:!block"
                        style={{ borderBottom: '1px solid #000' }}
                    >
                        Bill Date
                    </div>
                    <div
                        className="hidden py-2 font-bold uppercase text-black md:text-xs md2:!block"
                        style={{ borderBottom: '1px solid #000' }}
                    >
                        Order Number
                    </div>
                    <div
                        className="hidden py-2 pr-1 font-bold uppercase text-black md:text-xs md2:!block"
                        style={{ borderBottom: '1px solid #000' }}
                    >
                        Payment Progress
                    </div>
                    <div
                        className="hidden py-2 text-right font-bold uppercase text-black md:text-xs md2:!block"
                        style={{ borderBottom: '1px solid #000' }}
                    >
                        Amount
                    </div>

                    {/* Data Rows */}
                    {breakdownData.map((row) => {
                        const [current, total] = parseNoOfEasyPay(row.NoOfEasyPay);
                        const progressValue = total ? (current / total) * 100 : 0;

                        return (
                            <React.Fragment key={row.FullOrderNo}>
                                {/* Mobile/Tablet: 3 columns */}
                                <div className="pt-4 md2:hidden">
                                    <div className="flex gap-1">{row.NextBillDate}</div>
                                    <div className="underline">
                                        <a
                                            href={`/pages/myaccount/orderstatus?orderNo=${row.FullOrderNo}`}
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            {row.FullOrderNo}
                                        </a>
                                    </div>
                                </div>
                                <div className="pt-4 md2:hidden">
                                    <div className="flex items-center gap-2">
                                        <div className="w-2/3">
                                            <Progress value={isAnimating ? 0 : progressValue} />
                                        </div>
                                        <div className="flex w-1/2">
                                            <span>{row.NoOfEasyPay}</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="pt-4 text-right md2:hidden">
                                    $
                                    {row.AmountDue.toLocaleString('en-CA', {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                    })}
                                </div>

                                {/* Desktop: 4 columns */}
                                <div className="hidden gap-1 pt-4 md2:!flex">{row.NextBillDate}</div>
                                <div className="hidden pt-4 underline md2:!block">
                                    <a
                                        href={`/pages/myaccount/orderstatus?orderNo=${row.FullOrderNo}`}
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        {row.FullOrderNo}
                                    </a>
                                </div>
                                <div className="hidden pt-4 md2:!block">
                                    <div className="flex items-center gap-2">
                                        <div className="w-1/2">
                                            <Progress value={isAnimating ? 0 : progressValue} />
                                        </div>
                                        <div className="flex w-1/2">
                                            <span>{row.NoOfEasyPay}</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="hidden pt-4 text-right md2:!block">
                                    $
                                    {row.AmountDue.toLocaleString('en-CA', {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                    })}
                                </div>
                            </React.Fragment>
                        );
                    })}

                    {/* Total Row - Mobile */}
                    <div
                        className="mt-4 whitespace-nowrap py-2 font-bold uppercase leading-5 text-black md2:hidden"
                        style={{ borderBottom: '1px solid #000', borderTop: '1px solid #000' }}
                    >
                        Remaining Total:
                    </div>
                    <div
                        className="mt-4 py-2 font-bold uppercase leading-5 text-black md2:hidden"
                        style={{ borderBottom: '1px solid #000', borderTop: '1px solid #000' }}
                    ></div>
                    <div
                        className="mt-4 py-2 text-right font-bold uppercase leading-5 text-black md2:hidden"
                        style={{ borderBottom: '1px solid #000', borderTop: '1px solid #000' }}
                    >
                        {formattedTotal}
                    </div>

                    {/* Total Row - Desktop */}
                    <div
                        className="mt-4 hidden whitespace-nowrap py-2 font-bold uppercase leading-5 text-black md2:!block"
                        style={{ borderBottom: '1px solid #000', borderTop: '1px solid #000' }}
                    >
                        Remaining Total:
                    </div>
                    <div
                        className="mt-4 hidden py-2 font-bold uppercase leading-5 text-black md2:!block"
                        style={{ borderBottom: '1px solid #000', borderTop: '1px solid #000' }}
                    ></div>
                    <div
                        className="mt-4 hidden py-2 font-bold uppercase leading-5 text-black md2:!block"
                        style={{ borderBottom: '1px solid #000', borderTop: '1px solid #000' }}
                    ></div>
                    <div
                        className="mt-4 hidden py-2 text-right font-bold uppercase leading-5 text-black md2:!block"
                        style={{ borderBottom: '1px solid #000', borderTop: '1px solid #000' }}
                    >
                        {formattedTotal}
                    </div>
                </div>
            </div>
        </div>
    );
};
